import Header from "./components/Header";
import Hero from "./components/Hero";
import Life from "./components/Life";
import About from "./components/About";
import FraudStats from "./components/FraudStats";
import Howto from "./components/Howto";
import Pricing from "./components/Pricing";
import Option from "./components/Option";
import Support from "./components/Support";
import Flow from "./components/Flow";
import Join from "./components/Join";
import Qa from "./components/Qa";
import LineSupport from "./components/LineSupport";
import Footer from "./components/Footer";
import FloatingNav from "./components/FloatingNav";
import PcSidebar from "./components/PcSidebar";
import Compare from "./components/Compare";
import CookieConsent from "./components/CookieConsent";
import { useVariant } from "./context/VariantContext";
import { useEffect, useRef } from "react";
import { initGA4, trackScrollSection } from "./lib/gtag";
import { initLytag } from "./lib/lytag";
import { getConsent } from "./lib/consent";

function useSectionTracker() {
  const tracked = useRef(new Set<string>());
  useEffect(() => {
    const sections = document.querySelectorAll<HTMLElement>('section[id]');
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (entry.isIntersecting && !tracked.current.has(id)) {
            tracked.current.add(id);
            trackScrollSection(id);
          }
        });
      },
      { threshold: 0.3 },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);
}

function useSeoMeta() {
  const { seo } = useVariant();
  useEffect(() => {
    document.title = seo.title;
    const update = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    update('meta[name="description"]', 'content', seo.description);
    update('meta[property="og:title"]', 'content', seo.ogTitle);
    update('meta[property="og:description"]', 'content', seo.ogDescription);
    update('meta[property="og:image"]', 'content', seo.ogImage);
    update('meta[property="og:url"]', 'content', seo.canonical);
    update('meta[name="twitter:title"]', 'content', seo.twitterTitle);
    update('meta[name="twitter:description"]', 'content', seo.twitterDescription);
    update('meta[name="twitter:image"]', 'content', seo.ogImage);
    update('link[rel="canonical"]', 'href', seo.canonical);
  }, [seo]);
}

function initAnalytics() {
  initGA4();
  initLytag();
}

function App() {
  const { id, showOption, showSupport, showCompare, showFraudStats, pricingPhoto } = useVariant();
  useSectionTracker();
  useSeoMeta();

  useEffect(() => {
    if (getConsent() === 'granted') initAnalytics();
  }, []);

  return (
    <div className={`lp${id === 'therapy' ? ' lp--therapy' : ''}`}>
      <PcSidebar />
      <div className="lp__wrapper">
        <Header />
        <main>
          <Hero />
          <Life />
          <About />
          {showFraudStats && <FraudStats />}
          <Howto />
          <Pricing />
          <div className="lp__pricing-photo">
            <img
              className="lp__pricing-photo__round-top"
              src={pricingPhoto.roundTop}
              alt=""
            />
            <img
              className="lp__pricing-photo__img"
              src={pricingPhoto.image}
              alt=""
              loading="lazy"
            />
            <img
              className="lp__pricing-photo__round-bottom"
              src={pricingPhoto.roundBottom}
              alt=""
            />
          </div>
          {showOption && <Option />}
          {showCompare && <Compare />}
          {showSupport && <Support />}
          <Flow />
          <Join />
          <LineSupport />
          <Qa />
        </main>
        <Footer />
      </div>
      <FloatingNav />
      <CookieConsent onAccept={initAnalytics} />
    </div>
  );
}

export default App;
